import { useToast } from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";

// Consolidates the toast pattern used in 17 components:
//   const [toastMessage, setToastMessage] = useState(undefined);
//   const toast = useToast();
//   useEffect(() => {
//     if (toastMessage) {
//       toast({ title: toastMessage, status: "success", ... });
//     }
//     setToastMessage(undefined);
//   }, [toastMessage, toast]);
//
// Usage:
//   const showToast = useToastNotification();
//   showToast("Trade Updated Successfully");          // uses defaults
//   showToast("Couldn't save", { status: "error" });  // override per call
//
// Defaults match what every component already passes (solid + success +
// 10s + closable). Per-call overrides take precedence.
const DEFAULT_OPTIONS = {
  variant: "solid",
  status: "success",
  duration: 10000,
  isClosable: true,
};

export default function useToastNotification(defaults = {}) {
  const toast = useToast();
  const [pending, setPending] = useState(undefined);

  useEffect(() => {
    if (pending) {
      const { title, ...rest } = pending;
      toast({ ...DEFAULT_OPTIONS, ...defaults, ...rest, title });
    }
    if (pending !== undefined) {
      setPending(undefined);
    }
    // We intentionally exclude `defaults` from the dep list — it's a
    // per-render object literal in most call sites and including it would
    // re-fire the effect every render. Callers passing dynamic defaults
    // should memoize them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending, toast]);

  return useCallback((title, overrides = {}) => {
    if (title === undefined || title === null) return;
    setPending({ title, ...overrides });
  }, []);
}
